import React from 'react';
import { ArrowRight, Instagram, Linkedin, Mail, MessageCircle, Phone, Youtube } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { mockData } from '../../mock';
import { Reveal, SectionHeader } from './Shared';

export const NlFaq = () => (
  <section id="faq" data-testid="nl-faq" className="nl-section pt-0">
    <div className="nl-container grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <SectionHeader label="FAQ" title="Good questions." lead="Everything you need to know before your first game." />
        <Reveal delay={1} className="mt-8 text-sm text-slate-400">
          Still curious? <a href="mailto:info@spacehash.club" className="text-amber-500 hover:underline" data-testid="nl-faq-email">info@spacehash.club</a>
        </Reveal>
      </div>
      <Reveal className="lg:col-span-8 nl-accordion">
        <Accordion type="single" collapsible>
          {mockData.faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="nl-acc-item border-b-0">
              <AccordionTrigger className="nl-acc-trigger" data-testid={`nl-faq-trigger-${i}`}>{f.question}</AccordionTrigger>
              <AccordionContent className="nl-acc-content">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const NlFinalCta = ({ onBook, onEnquire }) => (
  <section data-testid="nl-final-cta" className="nl-section pt-0">
    <div className="nl-container">
      <Reveal className="relative overflow-hidden rounded-[2rem] border border-white/10 p-10 md:p-20 text-center">
        <div className="absolute inset-0 bg-gradient-to-br from-[#131720] via-[#0B0D11] to-[#1a1206]" />
        <div className="nl-glow w-[40rem] h-[20rem] bg-amber-500/25 left-1/2 -translate-x-1/2 -bottom-40" />
        <div className="relative">
          <span className="nl-label justify-center">Ready?</span>
          <h2 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-black uppercase leading-[0.9] tracking-tight">
            Hash your game<span className="text-amber-500">.</span>
          </h2>
          <p className="nl-lead mx-auto text-center mt-6">Courts are open 6 AM to 12 midnight, every day. Grab three friends or come solo — we'll find you a game.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button onClick={onBook} data-testid="nl-final-book-btn" className="nl-btn nl-btn-primary">Book a Court <ArrowRight className="w-4 h-4" /></button>
            <button onClick={() => onEnquire('')} data-testid="nl-final-coaching-btn" className="nl-btn nl-btn-ghost">Talk to a Coach</button>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const PARTNERS = [
  ['Adidas', 'https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg'],
  ['Babolat', 'https://customer-assets.emergentagent.com/job_ace-padel/artifacts/4h02j2cs_Babolat-Logo.png'],
  ['PiPlay', 'https://customer-assets.emergentagent.com/job_ace-padel/artifacts/yyt1y3t0_piplay_logo.webp'],
];

export const NlFooter = () => (
  <footer data-testid="nl-footer" className="border-t border-white/10 pt-16 pb-8">
    <div className="nl-container">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="nl-display text-4xl font-black">space<span className="text-amber-500">#</span></div>
          <p className="text-slate-400 text-sm mt-4 max-w-sm leading-relaxed">
            Where recreation meets community — inspiring an active lifestyle through innovative padel and pickle sports in India, through world-class facilities.
          </p>
          <div className="flex gap-3 mt-6">
            {[
              ['Instagram', Instagram, 'https://www.instagram.com/spacehash_kochi?igsh=MTJ1MTNpZ3YxYWkxMg=='],
              ['LinkedIn', Linkedin, '#'],
              ['YouTube', Youtube, '#'],
            ].map(([label, Icon, href]) => (
              <a key={label} href={href} target={href === '#' ? undefined : '_blank'} rel="noopener noreferrer" aria-label={label} data-testid={`nl-social-${label.toLowerCase()}`}
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-[#0B0D11] hover:bg-amber-500 hover:border-amber-500 transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Explore</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[['Why Padel', '#why-padel'], ['Locations', '#locations'], ['Gallery', '#gallery'], ['Coaching', '#coaching'], ['Franchise', '#franchise'], ['FAQ', '#faq']].map(([l, h]) => (
              <li key={h}><a href={h} className="text-slate-300 hover:text-amber-500 transition-colors">{l}</a></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Contact</h4>
          <ul className="mt-4 space-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-amber-500" /><a href="tel:+919895604656" data-testid="nl-footer-phone-1">+91 98956 04656</a></li>
            <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-amber-500" /><a href="tel:+919945710765" data-testid="nl-footer-phone-2">+91 99457 10765</a></li>
            <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-amber-500" /><a href="mailto:info@spacehash.club" data-testid="nl-footer-email">info@spacehash.club</a></li>
          </ul>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-6 flex-wrap justify-center">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Partners</span>
          {PARTNERS.map(([name, src]) => (
            <img key={name} src={src} alt={name} className="h-6 w-auto object-contain brightness-0 invert opacity-60 hover:opacity-100 transition-opacity" />
          ))}
          <span className="nl-display font-extrabold text-sm tracking-wider text-slate-400">SKYPADEL</span>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} space#. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export const NlWhatsApp = () => (
  <a
    href="https://wa.me/919895604656?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20Space%23%20Padel"
    target="_blank"
    rel="noopener noreferrer"
    className="nl-whatsapp"
    aria-label="Chat on WhatsApp"
    data-testid="nl-whatsapp-btn"
  >
    <MessageCircle className="w-6 h-6" />
  </a>
);
